import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Downshift from 'downshift'
import PropTypes from 'prop-types'
import matchSorter from 'match-sorter'
import { DebounceInput } from 'react-debounce-input'
import { changeSearchLocation, fetchSearchResultsIfNotBusy } from '../actions/search'
import { colors, fontFamilies } from '../../search-common/settings'
import { unique, media } from '../../search-common/helpers'
import { translate } from '../../modules/helper-functions'
import MapPin from '../icons/MapPin'
import QuickLinks from '../components/QuickLinks'
import { getLocationLabel, getSearchLabel } from '../helpers/locationLabels'

const Container = styled.div`
  padding: 24px;
  opacity: ${( props ) => props.disabled ? 0.3 : 1};
  height: max-content;
  transition: all 1s ease-in-out;
  color: ${colors.white};
`

// const Label = styled.label`
//   display: block;
//   font-family: ${fontFamilies.aeonik};
//   color: ${colors.grayTone3};
//   font-size: 16px;
//   margin-bottom: 12px;
// `

const List = styled.ul`
  list-style: none;
  background-color: transparent;
  color: white;
  max-height: 270px;
  height: auto;
  overflow-y: scroll;
  position: absolute;
  width: calc( 100% - 40px );
  background-color:${colors.blackTwo};
  z-index: 100;
`

const ListItem = styled.li`
  color: inherit;
  text-decoration: none;
  padding: 12px;
  line-height: 0;

  &:hover, &:active {
    background-color: ${colors.black};
  }

  svg {
    display: inline-block;
    fill: ${colors.grayTone3};
    grid-area: icon;
    width: 24px;
    height: 24px;
    ${media.tablet`
      width: 36px;
      height: 36px;
    `}
  }
`

const Location = styled.strong`
  display: inline;
  font-family: ${fontFamilies.aeonik};
  font-size: 14px;
  font-weight: bold;
  color: ${colors.green};
  letter-spacing: 0.5px;
  margin: 12px;
  vertical-align: 8px;
  ${media.tablet`
    vertical-align: 12px;
  `}

  &:after {
    content: "→";
    margin-left: 12px;
  }
`

const ClearButton = styled.img`
  content: url("/wp-content/themes/sponda/assets/src/img/icons/svg/close.svg");
  display: inline;
  width: 20px;
  margin: auto;
  margin-right: 10px;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 13px;
`

const FlexContainer = styled.span`
  width: 100%;
  ${media.tablet`
    display: flex;
  `}
`

const MapButtonContainer = styled.span`
  width: 100%;
  margin: 20px 0;
  ${media.tablet`
    margin: 0 10px;
  `}
  display: flex;
  flex-basis: 100px;
`

const EditButton = styled.span`
  flex: 1 1 50%;
  text-align: center;
  font-family: ${fontFamilies.aeonik};
  color: ${colors.green};
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid ${colors.highlight};
  padding: 12px 20px;
  display: inline-block;
  cursor: pointer;
  white-space: nowrap;
  ${( props ) => ( props.isActive && {
    backgroundColor: colors.green,
    color: colors.grayTone1
  })}
`

const InputContainer = styled.div`
  width: 100%;
  background-color: ${colors.white};
  position: relative;
`

const Input = styled( DebounceInput )`
  width: 100%;
  display: block;
  border: none;
  border-radius: 0;
  height: 46px;
  /* border-bottom: 1px solid ${( props ) => props.disabled ? colors.grayTone2 : colors.green}; */
  color: ${colors.black};
  padding: 6px;
  font-family: ${fontFamilies.aeonik};
  font-size: 16px;
  background: ${colors.white} url("/wp-content/themes/sponda/assets/src/img/icons/svg/location.svg") no-repeat 10px center;
  background-size: 20px;

  padding-left: 40px;



  &:focus {
    outline-color: none;
  }

  &:focus-visible {
    outline-color: ${colors.green};
    outline-style: solid;
  }
`

/**
 * Search component.
 */
class Search extends Component {
  /**
   * @constructor
   * @param {object} props
   */
  constructor( props ) {
    super( props )

    const { query, selectedItem } = this.props

    this.state = { value: ( selectedItem || query || '' ) }

    this.itemToString = this.itemToString.bind( this )
    this.handleInputChange = this.handleInputChange.bind( this )
    this.handleClear = this.handleClear.bind( this )
    this.maybeAutoFocus = this.maybeAutoFocus.bind( this )
    this.inputRef = React.createRef()
  }

  /**
   * Function returns a string representation
   * of the item.
   *
   * @param {string} item
   * @return {string}
   */
  itemToString( item ) {
    return item ? ( item.label || item.title ) : ''
  }

  /**
   * Handle input value change.
   *
   * @param {Event} event
   */
  handleInputChange( changes ) {
    const { doSearchQuery } = this.props
    if ( changes.selectedItem ) {
      this.setState({ value: changes.selectedItem })
      doSearchQuery( changes.selectedItem )
    } else if ( typeof changes.inputValue === 'string' ) {
      this.setState({ value: changes.inputValue })
      doSearchQuery({ type: 'custom', custom: changes.inputValue })
    }
  }

  /**
 * Handle input value change.
 *
 */
    handleClear() {
    const { doSearchQuery } = this.props
    this.setState({ value: '' })
    doSearchQuery()
  }

  /**
   * Conditionally focus to the search.
   */
  maybeAutoFocus() {
    this.inputRef && this.inputRef.current && this.inputRef.current.focus({
      preventScroll: true
    })
  }

  /**
   * Fire actions after component mounted.
   */
  componentDidMount() {
    this.maybeAutoFocus()
  }

    /**
   * Get the list of districts that are related
   * to the search results.
   */
  getItems( query ) {
    const { properties } = this.props

    const districts = properties.map(( property ) => {
      return {
        type: 'district',
        city: property.city,
        district: property.neighborhood,
        label: property.neighborhood + ', ' + property.city,
      }
    })

    const cities = properties.map(( property ) => {
      return {
        type: 'city',
        city: property.city,
        label: property.city,
      }
    })

    const locations = properties
      .filter(( property ) => property.location && property.location !== property.city )
      .map(( property ) => {
        return {
          type: 'location',
          location: property.location.split( ',' ).pop(),
          label: property.location.split( ',' ).pop() + ', ' + property.city,
        }
      })

    const items = [ ...unique( cities ), ...unique( districts ), ...unique( locations ) ]
    return matchSorter( items, query, {
      keys: [ 'label' ],
    })
  }

  /**
   * Render method.
   */
  render() {
    const { translations, doSearchQuery, quickLinks, setMapView, showMap } = this.props
    const { value = '' } = this.state
    return (
      <Downshift
        selectedItem={value}
        itemToString={this.itemToString}
        onStateChange={this.handleInputChange}
        >
        {({
          getRootProps,
          getItemProps,
          getInputProps,
          getMenuProps,
          isOpen,
          openMenu,
          highlightedIndex,
        }) => (
            <Container {...getRootProps()}>
              <FlexContainer>
                <InputContainer>
                  <Input {...getInputProps({
                    inputRef: this.inputRef,
                    maxLength: 100,
                    debounceTimeout: 300,
                    onFocus: openMenu,
                    placeholder: translate( translations, 'Find a property by city, area or postal code' )
                  })} />
                  <ClearButton onClick={this.handleClear} />
                  <List {...getMenuProps({ isOpen })}>
                      {isOpen && value.length > 0
                        ? this.getItems( value ).map(( item, index ) => (
                            <ListItem
                              key={index}
                              {...getItemProps({
                                item,
                                index,
                                isActive: highlightedIndex === index,
                              })}
                            >
                              <MapPin />
                              <Location>{item.label}</Location>
                            </ListItem>
                          ))
                      : null}
                    </List>
                  </InputContainer>
                  <MapButtonContainer>
                    <EditButton onClick={() => setMapView( false )} isActive={!showMap}>{translate( translations, 'List view' )}</EditButton>
                    <EditButton onClick={() => setMapView( true )} isActive={showMap}>{translate( translations, 'Map view' )}</EditButton>
                  </MapButtonContainer>
                </FlexContainer>
                <QuickLinks doSearchQuery={doSearchQuery} getMenuProps={getMenuProps} getItemProps={getItemProps} translations={translations} quickLinks={quickLinks} />
            </Container>
          )}
      </Downshift>
    )
  }
}

Search.propTypes = {
  query: PropTypes.string,
  selectedItem: PropTypes.object.isRequired,
  doSearchQuery: PropTypes.func.isRequired,
  setMapView: PropTypes.func.isRequired,
  showMap: PropTypes.bool.isRequired,
  properties: PropTypes.array.isRequired,
  translations: PropTypes.object.isRequired,
  quickLinks: PropTypes.array.isRequired,
}

/**
 * Map the redux state to component props.
 *
 * @param {object} state
 * @return {object}
 */
const mapStateToProps = ( state ) => ({
  query: state.search.query,
  selectedItem: getSelectedItem( state ),
  properties: state.search.properties,
  translations: state.translations,
  quickLinks: state.ui.quickLinks,
})

/**
 * Function maps the selected item from the
 * facets by the selected filter.
 *
 * @param {object} state
 */
const getSelectedItem = ( state ) => {
  const query = state.search.query
  if ( query ) return { type: 'query', value: query, label: getSearchLabel( query, state.ui.quickLinks ) }
  if ( state.search.filters.districts && state.search.filters.districts.length !== 0 ) {
    const city = state.search.filters.cities && state.search.filters.cities.length !== 0
      ? state.search.filters.cities[0]
      : ''
    const district = state.search.filters.districts[0]
    return {
      type: 'district',
      value: district,
      label: city ? `${district}, ${city}` : district
    }
  }
  if ( state.search.filters.cities && state.search.filters.cities.length !== 0 )
    return { type: 'city', value: state.search.filters.cities[0], label: state.search.filters.cities[0] }
  if ( state.search.filters.location && state.search.filters.location.length !== 0 )
    return {
      type: 'location',
      value: state.search.filters.location,
      label: getLocationLabel( state.search.filters.location, state.ui.quickLinks )
    }
  return {}
}

/**
 * Add dispatch functions to components props.
 *
 * @param {function} dispatch
 * @returns {object}
 */
const mapDispatchToProps = ( dispatch ) => {
  return {
    doSearchQuery: ( item ) => {
      dispatch( changeSearchLocation( item ))
      dispatch( fetchSearchResultsIfNotBusy())
    }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)( Search )
